import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { defaultPatientData } from '@components/layout/Modals/AddPatientModal/patientDataDefaults'
import membershipIconSrc from '@assets/images/Icons/Membership.svg'
import {
  ActionButtonIcons,
  ActionButtons,
  Badge,
  Button,
  DataTable,
  MetricCard,
  MetricCardGrid,
  Search,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as DeleteFilledIcon } from '@enamelai/design-system/icons/Delete2'
import {
  getMembershipOverviewForMember,
  MEMBERSHIP_PAGE_SIZE,
  MEMBERSHIP_PAGE_SIZE_OPTIONS,
  MEMBERSHIP_STATUS_BADGE_COLOR,
  membershipTierLabel,
  type MembershipKpiItem,
} from '@constants/Membership/membershipPage.constants'
import type { Patient } from '@dataTypes/PatientInfo.types'
import type { MembershipPageMember } from '@dataTypes/membership.type'
import { useTheme } from '@context/ThemeContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  getMembershipMemberKpis,
  getMembershipMembers,
} from '@store/actions/membership.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { setMembershipSidePanelOverview } from '@store/membershipSidePanelOverview.store'
import { setSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'
import AddMemberModal from './AddMemberModal.component'
import CancelMembershipModal from './CancelMembershipModal.component'
import UpdateMembershipPlanModal from './UpdateMembershipPlanModal.component'
import './Membership.css'

const MEMBERSHIP_ICON_STYLE = {
  '--membership-icon': `url(${membershipIconSrc})`,
} as React.CSSProperties

const GLOBAL_LOADING_CONTAINER = 'body'

const parseMembershipDate = (value: string): number => {
  const [month, day, year] = value.split('/').map(Number)
  if (!month || !day || !year) return 0
  return new Date(year, month - 1, day).getTime()
}

const compareMembershipValues = (
  left: string | number,
  right: string | number,
): number => {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

const findMembershipMemberForPatient = (
  patient: Patient,
  roster: MembershipPageMember[],
): MembershipPageMember | undefined => {
  const mockPrefix = 'mock-patient-membership-'
  if (patient.patient_id.startsWith(mockPrefix)) {
    const memberId = patient.patient_id.slice(mockPrefix.length)
    const byId = roster.find((member) => member.id === memberId)
    if (byId) return byId
  }

  const byPatientId = roster.find(
    (member) => member.patientId === patient.patient_id,
  )
  if (byPatientId) return byPatientId

  const fullName = `${patient.first_name} ${patient.last_name}`
    .trim()
    .toLowerCase()
  return roster.find((member) => member.name.trim().toLowerCase() === fullName)
}

const patientForMembershipRow = (
  member: MembershipPageMember,
  patients: Patient[],
): Patient => {
  const match =
    (member.patientId
      ? patients.find((patient) => patient.patient_id === member.patientId)
      : undefined) ??
    patients.find((patient) => {
      const fullName = `${patient.first_name} ${patient.last_name}`
        .trim()
        .toLowerCase()
      return fullName === member.name.trim().toLowerCase()
    })

  if (match) {
    return match
  }

  const [firstName, ...rest] = member.name.trim().split(/\s+/)
  return {
    ...defaultPatientData,
    patient_id: member.patientId ?? `mock-patient-membership-${member.id}`,
    first_name: firstName ?? member.name,
    last_name: rest.join(' '),
  }
}

const MembershipPageContent: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { resolvedTheme } = useTheme()
  const isDarkTheme = resolvedTheme === 'dark'
  const patients = useAppSelector((state) => state.patient.data)
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const selectedClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(MEMBERSHIP_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<string | undefined>('name')
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false)
  const [memberToUpdate, setMemberToUpdate] =
    useState<MembershipPageMember | null>(null)
  const [memberToCancel, setMemberToCancel] =
    useState<MembershipPageMember | null>(null)
  const [members, setMembers] = useState<MembershipPageMember[]>([])
  const [kpis, setKpis] = useState<MembershipKpiItem[]>([])
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const selectMembershipRow = useCallback(
    (row: MembershipPageMember): void => {
      setSelectedMemberId(row.id)
      setMembershipSidePanelOverview(getMembershipOverviewForMember(row))
      dispatch(
        setSelectedPatient({
          patient: patientForMembershipRow(row, patients),
        }),
      )
      dispatch(setActiveSidePanel('main'))
    },
    [dispatch, patients],
  )

  const requestMembership = useCallback(
    () =>
      Promise.all([
        dispatch(getMembershipMembers()),
        dispatch(getMembershipMemberKpis()),
      ]),
    [dispatch],
  )

  const applyMembership = useCallback(
    (
      membersResult: Awaited<ReturnType<typeof requestMembership>>[0],
      kpisResult: Awaited<ReturnType<typeof requestMembership>>[1],
    ): void => {
      if (getMembershipMembers.fulfilled.match(membersResult)) {
        setMembers(membersResult.payload.members)
      }
      if (getMembershipMemberKpis.fulfilled.match(kpisResult)) {
        setKpis(kpisResult.payload)
      }
    },
    [],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    void requestMembership()
      .then(([membersResult, kpisResult]) => {
        if (cancelled) return
        applyMembership(membersResult, kpisResult)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
      setMembershipSidePanelOverview(null)
    }
  }, [applyMembership, requestMembership, selectedClinicId])

  useEffect(() => {
    if (loading) {
      showLoading({
        container: GLOBAL_LOADING_CONTAINER,
        hideElements: [
          '.membership-page .membership-page__header + .membership-page__body',
        ],
      })
    } else {
      hideLoading(GLOBAL_LOADING_CONTAINER)
    }
    return () => hideLoading(GLOBAL_LOADING_CONTAINER)
  }, [loading, showLoading, hideLoading])

  const filteredMembers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return members

    return members.filter((member) => {
      const tierLabel = membershipTierLabel(member)
      return (
        member.name.toLowerCase().includes(query) ||
        tierLabel.toLowerCase().includes(query) ||
        member.status.toLowerCase().includes(query) ||
        member.paymentCycle.toLowerCase().includes(query)
      )
    })
  }, [members, searchQuery])

  const sortedMembers = useMemo(() => {
    if (!sortColumnId) return filteredMembers

    const direction = sortDirection === 'desc' ? -1 : 1

    return [...filteredMembers].sort((left, right) => {
      const getSortValue = (member: MembershipPageMember): string => {
        switch (sortColumnId) {
          case 'name':
            return member.name
          case 'tier':
            return membershipTierLabel(member)
          case 'memberSince':
          case 'expiryDate':
            return member[sortColumnId]
          case 'paymentCycle':
            return member.paymentCycle
          case 'status':
            return member.status
          default:
            return ''
        }
      }

      if (sortColumnId === 'memberSince' || sortColumnId === 'expiryDate') {
        return (
          (parseMembershipDate(getSortValue(left)) -
            parseMembershipDate(getSortValue(right))) *
          direction
        )
      }

      return (
        compareMembershipValues(getSortValue(left), getSortValue(right)) *
        direction
      )
    })
  }, [filteredMembers, sortColumnId, sortDirection])

  useEffect(() => {
    if (!selectedPatient) {
      setSelectedMemberId(null)
      setMembershipSidePanelOverview(null)
      return
    }

    const member = findMembershipMemberForPatient(selectedPatient, members)
    if (!member) {
      setSelectedMemberId(null)
      setMembershipSidePanelOverview(null)
      return
    }

    setSelectedMemberId(member.id)
    setMembershipSidePanelOverview(getMembershipOverviewForMember(member))

    const index = sortedMembers.findIndex((row) => row.id === member.id)
    if (index < 0) return
    const page = Math.floor(index / pageSize) + 1
    setCurrentPage((current) => (current === page ? current : page))
  }, [members, pageSize, selectedPatient, sortedMembers])

  const totalPages = Math.max(1, Math.ceil(sortedMembers.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)

  const pagedMembers = useMemo(() => {
    const start = (safePage - 1) * pageSize
    return sortedMembers.slice(start, start + pageSize)
  }, [pageSize, safePage, sortedMembers])

  const columns: DataTableColumn<MembershipPageMember>[] = useMemo(
    () => [
      {
        id: 'name',
        label: 'Members',
        sortable: true,
        renderCell: (row) => <span data-member-id={row.id}>{row.name}</span>,
      },
      {
        id: 'tier',
        label: 'Membership tier',
        sortable: true,
        renderCell: (row) => (
          <span className="membership-page__tier">
            <span
              className={`membership-page__tier-icon membership-page__tier-icon--${row.tier}`}
              style={MEMBERSHIP_ICON_STYLE}
              aria-hidden
            />
            <span className="membership-page__tier-label">
              {membershipTierLabel(row)}
            </span>
          </span>
        ),
      },
      {
        id: 'memberSince',
        label: 'Member since',
        sortable: true,
      },
      {
        id: 'expiryDate',
        label: 'Expiry date',
        sortable: true,
      },
      {
        id: 'paymentCycle',
        label: 'Payment cycle',
        sortable: true,
      },
      {
        id: 'status',
        label: 'Membership status',
        sortable: true,
        renderCell: (row) => (
          <Badge
            mode="tinted"
            color={MEMBERSHIP_STATUS_BADGE_COLOR[row.status]}
            label={row.status}
          />
        ),
      },
    ],
    [],
  )

  const handleSort = (columnId: string): void => {
    if (sortColumnId === columnId) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }

    setSortColumnId(columnId)
    setSortDirection('asc')
  }

  const handleTableClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement
    if (target.closest('button, a, input, label, [role="menu"]')) return
    const memberId = target
      .closest('tr.ds-data-table__row')
      ?.querySelector<HTMLElement>('[data-member-id]')
      ?.getAttribute('data-member-id')
    const member = memberId
      ? pagedMembers.find((row) => row.id === memberId)
      : undefined
    if (member) selectMembershipRow(member)
  }

  const handleSearchChange = (value: string): void => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handlePageSizeChange = (nextPageSize: number): void => {
    setPageSize(nextPageSize)
    setCurrentPage(1)
  }

  const handleOpenAddMember = (): void => {
    setIsAddMemberOpen(true)
  }

  const handleCloseAddMember = (): void => {
    setIsAddMemberOpen(false)
  }

  const handleMemberAdded = (): void => {
    void requestMembership().then(([membersResult, kpisResult]) => {
      applyMembership(membersResult, kpisResult)
    })
  }

  const handleOpenUpdatePlan = (member: MembershipPageMember): void => {
    setMemberToUpdate(member)
  }

  const handleCloseUpdatePlan = (): void => {
    setMemberToUpdate(null)
  }

  const handleOpenCancelMembership = (member: MembershipPageMember): void => {
    setMemberToCancel(member)
  }

  const handleCloseCancelMembership = (): void => {
    setMemberToCancel(null)
  }

  const handleMembershipCanceled = (): void => {
    void requestMembership().then(([membersResult, kpisResult]) => {
      applyMembership(membersResult, kpisResult)
    })
  }

  return (
    <div className="membership-page">
      <header className="membership-page__header">
        <h1 className="membership-page__title">Membership</h1>
        <div className="membership-page__header-actions">
          <Search
            className="membership-page__search"
            value={searchQuery}
            placeholder="Search"
            showFilter
            allowClearWithFilter
            onChange={handleSearchChange}
            onClear={() => handleSearchChange('')}
          />
          <Button variant="primary" size="md" onClick={handleOpenAddMember}>
            Add Member
          </Button>
        </div>
      </header>
      <div className="membership-page__body" onClick={handleTableClick}>
        <MetricCardGrid columns={5}>
          {kpis.map((kpi) => (
            <MetricCard
              key={kpi.id}
              label={kpi.label}
              value={kpi.value}
              unitInline={kpi.unitInline}
              footer={isDarkTheme ? (kpi.footerDark ?? kpi.footer) : kpi.footer}
              trend={kpi.trend}
              trendIcon="arrow"
              trendDirection={
                isDarkTheme
                  ? (kpi.trendDirectionDark ?? kpi.trendDirection)
                  : kpi.trendDirection
              }
            />
          ))}
        </MetricCardGrid>
        <DataTable
          className="membership-page__table"
          columns={columns}
          rows={pagedMembers}
          state={!loading && pagedMembers.length === 0 ? 'empty' : 'default'}
          selectedRowIds={selectedMemberId ? [selectedMemberId] : []}
          sortColumnId={sortColumnId}
          sortDirection={sortDirection}
          onSort={handleSort}
          actionsLabel="Action"
          renderActions={(row) => (
            <ActionButtons
              size="md"
              actions={[
                {
                  id: 'view',
                  label: 'View',
                  icon: ActionButtonIcons.view(),
                  tone: 'primary',
                  variant: 'solid',
                  onClick: () =>
                    navigate(`/membership/${row.id}`, {
                      state: { member: row },
                    }),
                },
                {
                  id: 'edit',
                  label: 'Edit',
                  icon: ActionButtonIcons.edit(),
                  tone: 'primary',
                  variant: 'outline',
                  disabled: row.disableEdit,
                  onClick: () => handleOpenUpdatePlan(row),
                },
                {
                  id: 'delete',
                  label: 'Delete',
                  icon: (
                    <DeleteFilledIcon
                      className="ds-action-button__svg"
                      aria-hidden="true"
                    />
                  ),
                  tone: 'danger',
                  variant: 'outline',
                  onClick: () => handleOpenCancelMembership(row),
                },
              ]}
            />
          )}
          pagination={{
            currentPage: safePage,
            totalPages,
            pageSize,
            pageSizeOptions: MEMBERSHIP_PAGE_SIZE_OPTIONS,
            onPageChange: setCurrentPage,
            onPageSizeChange: handlePageSizeChange,
          }}
          emptyState={{
            title: 'No members found',
            description: 'Try a different search or add a new member.',
          }}
        />
      </div>
      <AddMemberModal
        open={isAddMemberOpen}
        onClose={handleCloseAddMember}
        onAdded={handleMemberAdded}
      />
      <UpdateMembershipPlanModal
        open={Boolean(memberToUpdate)}
        member={memberToUpdate}
        onClose={handleCloseUpdatePlan}
        onUpdated={handleMemberAdded}
      />
      <CancelMembershipModal
        open={Boolean(memberToCancel)}
        member={memberToCancel}
        onClose={handleCloseCancelMembership}
        onCanceled={handleMembershipCanceled}
      />
    </div>
  )
}

export default MembershipPageContent
